import { FORUMS_BASE_URL } from '../../constants/endpoint'
import { IForumSearchResults, IForumTag, IForumThread, IForumCreateNewThreadResponse } from '../models/forums.model'
import { IAntiforgeryToken, IIdObject } from '../models/general.model'
import { IHasSubscription } from '../models/subscription.model'
import { getAntiforgeryToken } from './general.service'

export async function getForumSearchResult(
  query: string,
  pageIndex: number,
  tags?: string[],
): Promise<IForumSearchResults> {
  let response = await fetch(
    `${FORUMS_BASE_URL}/threads?query=${query}&pageIndex=${pageIndex}${tags && tags?.length > 0 ? `&tagIds=${tags}` : ''}`,
  )

  const jsonResults = await response.json()
  return <IForumSearchResults>jsonResults
}

export async function getForumTags(): Promise<IForumTag[]> {
  let response = await fetch(`${FORUMS_BASE_URL}/forumtags`)
  const jsonResults = await response.json()
  return <IForumTag[]>jsonResults
}

export async function getThread(forumThreadId: string): Promise<IForumThread> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${forumThreadId}/includeposts`)
  const jsonResults = await response.json()
  return <IForumThread>jsonResults
}

export async function updateForumThread(
  token: string,
  heading: string,
  text: string,
  tags: string[],

  threadId?: number,
): Promise<IForumCreateNewThreadResponse> {
  const bodyForum = JSON.stringify({
    Title: heading,
    Text: text,
    TagIds: tags,
  })

  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyForum,
  })
  const jsonResults = await response.json()
  return <IForumCreateNewThreadResponse>jsonResults
}

export async function createForumThread(
  token: string,
  heading: string,
  text: string,
  tags: string[],
): Promise<IForumCreateNewThreadResponse> {
  const bodyForum = JSON.stringify({
    Title: heading,
    Text: text,
    TagIds: tags,
  })

  let response = await fetch(`${FORUMS_BASE_URL}/threads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyForum,
  })
  const jsonResults = await response.json()
  return <IForumCreateNewThreadResponse>jsonResults
}

export async function createForumPost(token: string, forumThreadId: number, text: string) {
  const bodyForumPost = JSON.stringify({
    Text: text,
  })

  let response = await fetch(`${FORUMS_BASE_URL}/threads/${forumThreadId}/posts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyForumPost,
  })
  const jsonResults = await response.json()
  return <IIdObject>jsonResults
}

export async function updateForumPost(token: string, forumPostId: number, forumThreadId: number, text: string) {
  const bodyForumPost = JSON.stringify({
    Text: text,
  })

  let response = await fetch(`${FORUMS_BASE_URL}/threads/${forumThreadId}/posts/${forumPostId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
    body: bodyForumPost,
  })
  const jsonResults = await response.json()
  return <IIdObject>jsonResults
}

export async function deleteForumThread(token: string, threadId: number): Promise<number> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return response.status
}

export async function deleteForumPost(token: string, threadId: number, forumPostId: number): Promise<Boolean> {
  await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/posts/${forumPostId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

export async function unlockForumThread(token: string, threadId: number): Promise<boolean> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/unlock`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function lockforumthread(token: string, threadId: number): Promise<boolean> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/lock`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function addSubscription(token: string, threadId: number): Promise<boolean> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function hasUserSubscribedToThread(forumThreadId: string): Promise<IHasSubscription> {
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${forumThreadId}/subscribe`)
  const isSubscribing = await response.json()
  return <IHasSubscription>isSubscribing
}

export async function removeSubscription(token: string, threadId: string): Promise<Boolean> {
  await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/subscribe`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': token,
    },
  })
  return true
}

export async function getForumThreads(
  pageIndex: number,
  showOnlyNotAssignedThreads: boolean,
  userId?: number,
): Promise<IForumSearchResults> {
  let response = await fetch(
    `${FORUMS_BASE_URL}/threads?pageIndex=${pageIndex}${userId != null ? `&assignedToUserId=${userId}&isLocked=false` : ''}${showOnlyNotAssignedThreads ? `&showOnlyNotAssignedThreads=${showOnlyNotAssignedThreads}&isLocked=false` : ''}&pageSize=10`,
  )

  const jsonResults = await response.json()
  return <IForumSearchResults>jsonResults
}

export async function getForumUnpublishedThreads(pageIndex: number, isArchived: boolean): Promise<IForumSearchResults> {
  let response = await fetch(
    `${FORUMS_BASE_URL}/threads?pageIndex=${pageIndex}&${isArchived ? 'isArchived=true' : 'isPendingArchiving=true'}&pageSize=10`,
  )

  const jsonResults = await response.json()
  return <IForumSearchResults>jsonResults
}

export async function assignUserToThread(userId: number, threadId: number) {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  const bodyForumUser = JSON.stringify({
    userId: userId,
  })
  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/assignuser`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: bodyForumUser,
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function unassignUserToThread(threadId: number) {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/unassignuser`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}

export async function reviewForumThread(threadId: number): Promise<Boolean> {
  const { csrfToken }: IAntiforgeryToken = await getAntiforgeryToken()

  let response = await fetch(`${FORUMS_BASE_URL}/threads/${threadId}/review`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
  })
  const jsonResults = (await response.ok) ?? response.ok
  return jsonResults
}
